import type { Dictionary } from '@/i18n/dictionary'
import { mediusTermsEn } from '@/i18n/medius-terms'
import { mediusDeletionEn, mediusPrivacyEn } from '@/i18n/medius-policies'
import { sparsaAcceptableUseEn, sparsaTermsEn } from '@/i18n/sparsa-terms'
import { sparsaDeletionEn, sparsaPrivacyEn } from '@/i18n/sparsa-policies'

export const sl: Dictionary = {
  meta: {
    home: {
      title: 'Dalmos Systems — Programska oprema po meri, CRM in integracije',
      description:
        'Dalmos Systems razvija poslovno programsko opremo po meri za finance, nepremičnine, gostinstvo in logistiko — integracije CRM, ERP in PMS, oblačno infrastrukturo in dolgoročno vzdrževanje.',
      keywords: [
        'programska oprema po meri',
        'uvedba CRM',
        'programska oprema za nepremičnine',
        'programska oprema za hotelirstvo in gostinstvo',
        'integracije ERP in PMS',
        'API integracije',
        'Microsoft Dynamics 365',
        'Power Platform',
        'razvoj programske opreme',
      ],
    },
    privacy: {
      title: 'Pravilnik o zasebnosti — Dalmos Systems',
      description:
        'Kako Dalmos Systems obdeluje osebne podatke, zbrane prek kontaktnega obrazca, skladno z GDPR.',
    },
    legal: {
      title: 'Pravno obvestilo — Dalmos Systems',
      description:
        'Pravni podatki o podjetju Dalmos Systems: registrirano ime, sedež v Šibeniku, vpis v hrvaški obrtni register in kontaktni podatki.',
    },
    mediusPrivacy: {
      title: 'Medius – pravilnik o zasebnosti (angleško) — Dalmos Systems',
      description:
        'Kako Medius podjetja Dalmos Systems obdeluje osebne podatke v imenu svojih strank. Pravilnik je na voljo v angleščini in hrvaščini.',
    },
    mediusDeletion: {
      title: 'Medius – izbris podatkov (angleško) — Dalmos Systems',
      description:
        'Kako zahtevati izbris osebnih podatkov iz storitve Medius, tudi sporočil, prejetih prek Facebook Messengerja, Instagrama in WhatsAppa.',
    },
    mediusTerms: {
      title: 'Medius – pogoji uporabe (angleško) — Dalmos Systems',
      description:
        'Pogoji, pod katerimi Dalmos Systems poslovnim strankam zagotavlja Medius. Pogoji so na voljo v angleščini in hrvaščini.',
    },
    sparsaPrivacy: {
      title: 'Sparsa – pravilnik o zasebnosti (angleško) — Dalmos Systems',
      description:
        'Kako Sparsa podjetja Dalmos Systems obdeluje osebne podatke prejemnikov e-novic. Pravilnik je na voljo v angleščini in hrvaščini.',
    },
    sparsaDeletion: {
      title: 'Sparsa – izbris podatkov (angleško) — Dalmos Systems',
      description:
        'Kako se odjaviti od e-novic, poslanih prek storitve Sparsa, in kako zahtevati izbris osebnih podatkov.',
    },
    sparsaTerms: {
      title: 'Sparsa – pogoji uporabe (angleško) — Dalmos Systems',
      description:
        'Pogoji, pod katerimi Dalmos Systems poslovnim strankam zagotavlja Sparso, storitev za pošiljanje e-novic. Pogoji so na voljo v angleščini in hrvaščini.',
    },
    sparsaAcceptableUse: {
      title: 'Sparsa – sprejemljiva uporaba (angleško) — Dalmos Systems',
      description:
        'Pravila pošiljanja s storitvijo Sparsa: privolitev vsakega prejemnika, brez kupljenih seznamov, izjava ob uvozu in prijava zlorab.',
    },
    notFound: {
      title: 'Strani ni mogoče najti — Dalmos Systems',
      description: 'Zahtevane strani nismo našli.',
    },
  },
  nav: {
    home: 'Domov',
    services: 'Storitve',
    about: 'O nas',
    contact: 'Kontakt',
    cta: 'Zahtevajte ponudbo',
    openMenu: 'Odprite navigacijo',
    closeMenu: 'Zaprite navigacijo',
    languageLabel: 'Izberite jezik',
    currentLanguage: 'Trenutni jezik',
  },
  a11y: {
    skipToContent: 'Preskočite na vsebino',
    decorative: 'Dekorativni element',
  },
  hero: {
    eyebrow: 'Programske rešitve po meri za podjetja',
    headlineBefore: 'Sistemi, ki delajo za vaše podjetje,',
    headlineAccent: 'in ne obratno.',
    subtitle:
      'Tehnični partner za poslovno programsko opremo po meri — rešitve za finance, nepremičnine, gostinstvo in logistiko, zgrajene na izkušnjah z velikih projektov CRM, z Microsoftovim ekosistemom kot zanesljivim temeljem in ne edinim poudarkom.',
    ctaPrimary: 'Zahtevajte ponudbo',
    ctaSecondary: 'Oglejte si storitve',
    chips: [
      'Integracije CRM in ERP',
      'Gostinstvo in PMS',
      'Finančne rešitve',
      'Nepremičnine',
      'Mobilne aplikacije',
      'Skladišče in upravljanje objektov',
    ],
    scrollHint: 'Pomaknite se navzdol',
  },
  stats: {
    title: 'Na čem gradimo',
    items: [
      { value: '20+', label: 'Področij storitev pod eno streho' },
      { value: '6', label: 'Panog s preverjenimi vzorci rešitev' },
      { value: '1', label: 'Oseba, ki vodi projekt in piše kodo' },
      { value: '100%', label: 'Projektov gre v uporabo z opredeljeno podporo' },
    ],
  },
  why: {
    title: 'Zakaj Dalmos Systems',
    cards: [
      {
        title: 'Neposreden stik',
        body: 'Brez agencijskih vmesnih plasti — govorite neposredno z osebo, ki projekt tudi razvija.',
      },
      {
        title: 'Rešitve, prilagojene panogi',
        body: 'Od financ in nepremičnin do gostinstva in logistike — vsak sistem nastane okoli vaših dejanskih procesov.',
      },
      {
        title: 'Partner, ne izvajalec',
        body: 'Pri sistemu ostanemo tudi po predaji — vzdrževanje, nadgradnje in podpora so del dogovora.',
      },
    ],
  },
  services: {
    title: 'Širok nabor rešitev, en vir',
    lede: 'Od financ in nepremičnin do gostinstva in oblačne infrastrukture — vsaka storitev vključuje uvedbo in dolgoročno vzdrževanje, brez pretiranega poudarka na eni ozki niši.',
    items: {
      crm: {
        title: 'CRM in prilagoditve',
        body: 'Uvedba in prilagoditev sistemov CRM vašim procesom, ekipam in strankam.',
      },
      erp: {
        title: 'Sistemi ERP in integracije',
        body: 'Povezovanje ERP-ja s prodajo, skladiščem in financami ter prilagoditve obstoječih modulov.',
      },
      processes: {
        title: 'Avtomatizacija in optimizacija procesov',
        body: 'Avtomatizacija notranjih delovnih tokov in aplikacije, ki pospešijo odločanje in zmanjšajo ročno delo.',
      },
      finance: {
        title: 'Finančne rešitve',
        body: 'Sistemi za spremljanje stroškov, obračun in finančno poročanje.',
      },
      realEstate: {
        title: 'Nepremičnine in upravljanje premoženja',
        body: 'Vodenje portfelja nepremičnin, najemnih pogodb in cenitev.',
      },
      mobile: {
        title: 'Mobilne aplikacije',
        body: 'Mobilne razširitve poslovnih sistemov za terensko delo, skladišče ali prodajo.',
      },
      hospitality: {
        title: 'Rešitve za gostinstvo',
        body: 'Sistemi za hotele, restavracije in nastanitve — integracije PMS, rezervacije, podatki o gostih in operativa.',
      },
      web: {
        title: 'Spletne rešitve in portali',
        body: 'Spletne strani, usklajene z vašim poslovnim sistemom.',
      },
      reservations: {
        title: 'Rezervacijski sistemi',
        body: 'Spletne rezervacije v realnem času, povezane s PMS-jem in obračunom.',
      },
      analytics: {
        title: 'Statistika in poročila',
        body: 'Nadzorne plošče za odločitve na podlagi dejanskih podatkov.',
      },
      loyalty: {
        title: 'Sistemi zvestobe',
        body: 'Programi zvestobe s točkami, ravnmi in nagradami, članskimi karticami ter mobilno aplikacijo ali kartico v digitalni denarnici. Ponudbe in kampanje sledijo zgodovini nakupov vsakega člana, vse pa ostane povezano z vašim sistemom CRM.',
      },
      marketing: {
        title: 'Marketing in e-novice',
        body: 'E-novice in kampanje za stike iz vašega sistema CRM, z vgrajenimi soglasji in odjavami.',
      },
      communication: {
        title: 'Komunikacijske rešitve',
        body: 'Rešitve po meri za komunikacijo vaše ekipe s strankami, povezane z vašim sistemom CRM ali samostojne.',
      },
      warehouse: {
        title: 'Upravljanje skladišča',
        body: 'Spremljanje zalog in gibanja blaga.',
      },
      facility: {
        title: 'Upravljanje objektov',
        body: 'Upravljanje objektov in vzdrževanja.',
      },
      api: {
        title: 'Integracije API in middleware',
        body: 'Povezovanje CRM, ERP, PMS in drugih sistemov, ki se ne pogovarjajo med seboj.',
      },
      cloud: {
        title: 'Oblačna infrastruktura in DevOps',
        body: 'Okolja Azure, CI/CD in avtomatizacija.',
      },
      microsoft: {
        title: 'Microsoftov ekosistem',
        body: 'Dynamics 365, Power Platform in Azure, kadar so prava izbira za vaše poslovanje.',
      },
      security: {
        title: 'Informacijska varnost in skladnost',
        body: 'Varstvo podatkov in skladnost z GDPR.',
      },
      custom: {
        title: 'Programske rešitve po meri',
        body: 'Rešitve po meri za posebne potrebe.',
      },
      training: {
        title: 'Izobraževanje in uvajanje',
        body: 'Usposabljanje uporabnikov in interna dokumentacija.',
      },
      support: {
        title: 'Podpora in pogodbe SLA',
        body: 'Opredeljeni odzivni časi in časi podpore.',
      },
    },
  },
  integrations: {
    title: 'Sistemi, ki se končno pogovarjajo',
    lede: 'CRM, ERP in PMS se le redko povežejo sami od sebe. Povežemo jih tako, da podatke vnesete enkrat in jih vidite povsod — brez ročnega prepisovanja in preglednic vmes.',
    hubLabel: 'En vir podatkov',
    systems: [
      {
        code: 'CRM',
        title: 'Stranke in prodaja',
        body: 'Stranke, priložnosti in komunikacija na enem mestu, usklajene s ponudbami, pogodbami in obračunom.',
      },
      {
        code: 'ERP',
        title: 'Finance in operativa',
        body: 'Fakturiranje, nabava in zaloge, povezani s prodajo in skladiščem, brez dvojnega vnosa.',
      },
      {
        code: 'PMS',
        title: 'Gostinstvo',
        body: 'Sistemi za upravljanje nastanitev, povezani z rezervacijami, CRM-jem in računovodstvom.',
      },
      {
        code: 'API',
        title: 'Vse drugo',
        body: 'Računovodstvo, spletna trgovina, POS, channel managerji, orodja BI in interne aplikacije.',
      },
    ],
    capabilitiesTitle: 'Kaj dobite',
    capabilities: [
      'Dvosmerna sinhronizacija podatkov v realnem času',
      'Selitve podatkov brez izpada',
      'Middleware in API-ji po meri',
      'Nadzor, dnevniki in opozorila, ko se kaj zatakne',
    ],
  },
  about: {
    title: 'O nas',
    statementBefore: 'Novo podjetje.',
    statementAccent: 'Izkušnje, ki se ne začnejo od ničle.',
    lede: 'Podjetje Dalmos Systems je bilo ustanovljeno kot tehnični partner za poslovno programsko opremo po meri — finance, nepremičnine, gostinstvo, logistika in več. Pristop je neposredno nadaljevanje dela na velikih projektih migracije in integracije sistemov CRM v nepremičninskem in hotelskem sektorju, brez velike organizacije vmes.',
    processTitle: 'Kako delamo',
    steps: [
      { index: '01', title: 'Analiza', body: 'Razumevanje procesov pred prvo vrstico kode.' },
      {
        index: '02',
        title: 'Zasnova rešitve',
        body: 'Arhitektura, prilagojena vašim sistemom, in ne obratno.',
      },
      {
        index: '03',
        title: 'Izvedba',
        body: 'Razvoj v iteracijah z rednimi usklajevanji z vami.',
      },
      { index: '04', title: 'Podpora', body: 'Pri sistemu ostanemo tudi po zagonu.' },
    ],
    valuesTitle: 'Kaj nas vodi',
    values: [
      {
        title: 'Neposreden stik',
        body: 'S stranko se pogovarja oseba, ki projekt tudi vodi, brez komunikacijskih plasti.',
      },
      {
        title: 'Preglednost',
        body: 'Jasen obseg, jasna cena in jasne meje projekta pred prvo vrstico kode.',
      },
      {
        title: 'Dolgoročnost',
        body: 'Gradimo sisteme, ki jih bomo vzdrževali sami, ne projektov, ki jih pustimo za sabo.',
      },
    ],
  },
  contact: {
    title: 'Pogovorimo se o vašem sistemu',
    lede: 'Oglasite se z opisom projekta — odgovorimo s predlogom naslednjega koraka.',
    responseNote: 'Odgovorimo v {days} delovnih dneh.',
    emailLabel: 'E-pošta',
    phoneLabel: 'Telefon',
    form: {
      name: 'Ime in priimek',
      namePlaceholder: 'Ana Novak',
      email: 'E-pošta',
      emailPlaceholder: 'ana@podjetje.si',
      company: 'Podjetje',
      companyPlaceholder: 'Naziv podjetja (ni obvezno)',
      message: 'Sporočilo',
      messagePlaceholder: 'Opišite projekt, sisteme, ki jih uporabljate, in želeni rok.',
      consent: 'Soglašam, da se moji podatki uporabijo za odgovor na to povpraševanje.',
      submit: 'Pošljite povpraševanje',
      submitting: 'Pošiljamo…',
      successTitle: 'Povpraševanje je poslano.',
      successBody: 'Oglasili se bomo na navedeni e-poštni naslov.',
      errorTitle: 'Pošiljanje ni uspelo.',
      errorBody: 'Poskusite znova ali nam pišite neposredno po e-pošti.',
      fallbackHint: 'Odprite e-poštni odjemalec',
      errors: {
        nameRequired: 'Vnesite ime in priimek.',
        emailRequired: 'Vnesite e-poštni naslov.',
        emailInvalid: 'Ta e-poštni naslov ni veljaven.',
        messageRequired: 'Vnesite sporočilo.',
        messageTooShort: 'Sporočilo mora imeti vsaj 20 znakov.',
        consentRequired: 'Potrebno je soglasje za obdelavo podatkov.',
      },
    },
  },
  footer: {
    tagline:
      'Poslovna programska oprema po meri za finance, nepremičnine, gostinstvo in logistiko.',
    navTitle: 'Navigacija',
    legalTitle: 'Pravne informacije',
    privacy: 'Pravilnik o zasebnosti',
    legalNotice: 'Pravno obvestilo',
    rights: 'Vse pravice pridržane.',
    builtIn: 'Ulica Sedmog kontinenta 1, 22000 Šibenik, Hrvaška',
  },
  privacy: {
    title: 'Pravilnik o zasebnosti',
    lastUpdatedLabel: 'Zadnja sprememba',
    lastUpdated: '2026-10-31',
    intro:
      'Ta pravilnik opisuje, kako Dalmos Systems obdeluje osebne podatke, ki nam jih pošljete prek tega spletnega mesta.',
    sections: [
      {
        heading: 'Upravljavec',
        paragraphs: [
          'Upravljavec je {controller}. Za vsa vprašanja o obdelavi podatkov nam pišite na {email}.',
        ],
      },
      {
        heading: 'Katere podatke zbiramo',
        paragraphs: [
          'Prek kontaktnega obrazca zbiramo ime in priimek, e-poštni naslov, naziv podjetja, če ga vnesete, in vsebino vašega sporočila.',
          'Ne uporabljamo oglaševalskih piškotkov niti orodij za sledenje obiskovalcem po drugih spletnih mestih.',
          'Obiske štejemo s storitvijo Cloudflare Web Analytics, Cloudflarovim skriptom brez piškotkov (static.cloudflareinsights.com). Ko se stran naloži, skript Cloudflaru pošlje anonimne podatke o ogledu in delovanju strani: naslov strani, stran, s katere ste prišli, ter čase nalaganja in podobne meritve delovanja strani; brskalnik, operacijski sistem, vrsto naprave in državo Cloudflare doda iz same zahteve. Skript ne nastavlja piškotkov, ne uporablja druge shrambe v vaši napravi in ne ustvari identifikatorja obiskovalca. Vaš naslov IP pride do Cloudflara z zahtevo, kot pri vsakem spletnem mestu; Cloudflare ga ne uporablja za vašo prepoznavo in ga v ta namen ne shranjuje. Vidimo le skupne številke, na primer oglede strani, spletna mesta, s katerih obiskovalci prihajajo, države in vrste naprav, in jih uporabljamo, da razumemo, kako se spletno mesto uporablja, in da ostane hitro. Pravna podlaga je naš zakoniti interes za delovanje in izboljševanje spletnega mesta (točka (f) prvega odstavka 6. člena GDPR). Cloudflare podatke za nas obdeluje kot obdelovalec, po svoji pogodbi o obdelavi podatkov s standardnimi pogodbenimi klavzulami EU za prenose zunaj EU. Celotne podatke hrani 7 dni, nato pa vzorec približno desetine, iz katerega vidimo statistiko za zadnjih šest mesecev. Skript lahko blokirate v nastavitvah brskalnika ali z orodjem za blokiranje vsebin; spletno mesto brez njega deluje enako. Enako velja za spletno mesto Medius, medius.dalmossystems.com, ki se sklicuje na ta pravilnik.',
        ],
      },
      {
        heading: 'Namen in pravna podlaga',
        paragraphs: [
          'Podatke uporabljamo izključno za odgovor na vaše povpraševanje in pripravo ponudbe. Pravna podlaga je vaše soglasje skupaj z našim zakonitim interesom za komunikacijo z morebitnimi strankami.',
        ],
      },
      {
        heading: 'Rok hrambe',
        paragraphs: [
          'Povpraševanja hranimo največ štiriindvajset mesecev od zadnje komunikacije, nato jih izbrišemo, razen če je daljša hramba potrebna zaradi pogodbene ali zakonske obveznosti.',
        ],
      },
      {
        heading: 'Vaše pravice',
        paragraphs: [
          'Imate pravico do dostopa do podatkov, popravka, izbrisa, omejitve obdelave, prenosljivosti podatkov in pravico, da soglasje kadar koli prekličete.',
          'Pritožbo lahko vložite pri nadzornem organu za varstvo podatkov; za obdelavo na Hrvaškem je to Agencija za varstvo osebnih podatkov (AZOP).',
        ],
      },
      {
        heading: 'Varnost in prenos podatkov',
        paragraphs: [
          'Promet med vašim brskalnikom in našimi strežniki je šifriran. Podatkov ne prodajamo in jih ne delimo s tretjimi osebami, razen s ponudniki storitev, ki nam pomagajo pri delovanju spletnega mesta in komunikaciji, na podlagi pogodbe o obdelavi podatkov.',
        ],
      },
    ],
    backHome: 'Nazaj na domačo stran',
  },
  legal: {
    title: 'Pravno obvestilo',
    intro: 'Podatki o ponudniku storitev v skladu s hrvaškim zakonom o elektronskem poslovanju.',
    nameLabel: 'Registrirano ime',
    shortNameLabel: 'Kratko ime',
    seatLabel: 'Sedež',
    country: 'Hrvaška',
    registerLabel: 'Register',
    registerValue: 'Hrvaški obrtni register (Obrtni registar), Šibeniško-kninska županija',
    mboLabel: 'Matična številka obrti (MBO)',
    registeredOnLabel: 'Datum vpisa',
    activityLabel: 'Glavna dejavnost',
    activityValue:
      'Izdelava, razvoj in vzdrževanje računalniških programov in aplikacij po naročilu (NKD 2025: 62.10.9)',
    contactLabel: 'Kontakt',
    backHome: 'Nazaj na domačo stran',
  },
  mediusPrivacy: {
    ...mediusPrivacyEn,
    lastUpdatedLabel: 'Velja od',
    languageNote:
      'Ta pravilnik za Medius je na voljo v angleščini in hrvaščini; spodaj je angleška različica.',
    backHome: 'Nazaj na domačo stran',
  },
  mediusDeletion: {
    ...mediusDeletionEn,
    lastUpdatedLabel: 'Velja od',
    languageNote:
      'Ta navodila so na voljo v angleščini in hrvaščini; spodaj je angleška različica.',
    backHome: 'Nazaj na domačo stran',
  },
  mediusTerms: {
    ...mediusTermsEn,
    lastUpdatedLabel: 'Velja od',
    languageNote:
      'Ti pogoji za Medius so na voljo v angleščini in hrvaščini; spodaj je angleška različica.',
    backHome: 'Nazaj na domačo stran',
  },
  mediusLinks: {
    mediusPrivacy: 'Medius – pravilnik o zasebnosti',
    mediusDeletion: 'Kako zahtevati izbris podatkov',
    mediusTerms: 'Medius – pogoji uporabe',
  },
  sparsaPrivacy: {
    ...sparsaPrivacyEn,
    lastUpdatedLabel: 'Velja od',
    languageNote:
      'Ta pravilnik za Sparso je na voljo v angleščini in hrvaščini; spodaj je angleška različica.',
    backHome: 'Nazaj na domačo stran',
  },
  sparsaDeletion: {
    ...sparsaDeletionEn,
    lastUpdatedLabel: 'Velja od',
    languageNote:
      'Ta navodila so na voljo v angleščini in hrvaščini; spodaj je angleška različica.',
    backHome: 'Nazaj na domačo stran',
  },
  sparsaTerms: {
    ...sparsaTermsEn,
    lastUpdatedLabel: 'Velja od',
    languageNote:
      'Ti pogoji za Sparso so na voljo v angleščini in hrvaščini; spodaj je angleška različica.',
    backHome: 'Nazaj na domačo stran',
  },
  sparsaAcceptableUse: {
    ...sparsaAcceptableUseEn,
    lastUpdatedLabel: 'Velja od',
    languageNote:
      'Ta pravila sprejemljive uporabe so na voljo v angleščini in hrvaščini; spodaj je angleška različica.',
    backHome: 'Nazaj na domačo stran',
  },
  sparsaLinks: {
    sparsaPrivacy: 'Sparsa – pravilnik o zasebnosti',
    sparsaDeletion: 'Kako se odjaviti in izbrisati podatke',
    sparsaTerms: 'Sparsa – pogoji uporabe',
    sparsaAcceptableUse: 'Sparsa – pravila sprejemljive uporabe',
  },
  notFound: {
    code: '404',
    title: 'Strani ni mogoče najti',
    body: 'Naslov, ki ste ga odprli, ne obstaja ali je bil premaknjen.',
    cta: 'Nazaj na domačo stran',
  },
}
